import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux/es/hooks/useSelector";
import { useNavigate } from "react-router-dom";
import NewBuyerDashboard from "../../BuyerTabs/DashboardTab/NewBuyerDashboardV2";
import { API_ENDPOINT } from "../../../services/api/resources";

const DEFAULT_ORG_TOKEN = "65a58dee88d94083987f9cfaa9428423";
const DASHBOARD_REQUEST_TIMEOUT = 60000;

const getOrgToken = (loginData = {}) =>
  loginData?.org_token ||
  loginData?.organization_token ||
  loginData?.user?.org_token ||
  loginData?.user?.organization_token ||
  loginData?.user?.token ||
  DEFAULT_ORG_TOKEN;

const extractPrSection = (payload = {}) => {
  const root = payload?.response || payload?.data || payload || {};
  return (
    root?.dashboard1?.Module?.PR ||
    root?.dashboard1?.PR ||
    root?.Module?.PR ||
    root?.PR ||
    root
  );
};

const mergePrDetails = (dashboardRoot = {}, prPayload = {}) => {
  const prSection = extractPrSection(prPayload);
  if (!prSection || typeof prSection !== "object" || !Object.keys(prSection).length) {
    return dashboardRoot;
  }

  const currentDashboard = dashboardRoot || {};
  return {
    ...currentDashboard,
    dashboard1: {
      ...(currentDashboard.dashboard1 || {}),
      Module: {
        ...(currentDashboard.dashboard1?.Module || {}),
        PR: prSection,
      },
    },
    budget_savings_analytics:
      prPayload?.response?.budget_savings_analytics ||
      prPayload?.budget_savings_analytics ||
      currentDashboard?.budget_savings_analytics ||
      {},
    location_wise_pr_raised:
      prPayload?.response?.location_wise_pr_raised ||
      prPayload?.location_wise_pr_raised ||
      currentDashboard?.location_wise_pr_raised ||
      [],
  };
};

const extractAssetSummary = (payload = {}) => payload?.response || payload?.data || payload || {};

const mergeAssetSummary = (dashboardRoot = {}, assetPayload = {}) => {
  const assetSection = extractAssetSummary(assetPayload);
  if (!assetSection || typeof assetSection !== "object" || !Object.keys(assetSection).length) {
    return dashboardRoot;
  }

  const currentDashboard = dashboardRoot || {};
  return {
    ...currentDashboard,
    dashboard8: {
      ...(currentDashboard.dashboard8 || {}),
      ...assetSection,
    },
  };
};

const BuyerAdminDashboardTab = () => {
  const loginData = useSelector((state) => state.loginData.value);
  const navigate = useNavigate();
  const [dashboardResponse, setDashboardResponse] = useState({});
  const [loadingState, setLoadingState] = useState(false);

  const authToken = loginData?.auth_token;
  const orgToken = useMemo(() => getOrgToken(loginData), [loginData]);

  const loadDashboard = useCallback(async () => {
    if (!orgToken) return;

    setLoadingState(true);
    try {
      const headers = authToken
        ? { Authorization: `Bearer ${authToken}` }
        : undefined;

      const dashboardRequest = axios.post(
        API_ENDPOINT.buyer_admin_dashboard,
        { org_token: orgToken },
        { headers, timeout: DASHBOARD_REQUEST_TIMEOUT }
      );

      const prDetailsRequest = axios.post(
        API_ENDPOINT.get_pr_details_by_org_token,
        { org_token: orgToken },
        { headers, timeout: DASHBOARD_REQUEST_TIMEOUT }
      );

      const assetSummaryRequest = axios.post(
        API_ENDPOINT.get_asset_summary,
        { org_token: orgToken },
        { headers, timeout: DASHBOARD_REQUEST_TIMEOUT }
      );

      const [dashboardResult, prResult, assetResult] = await Promise.allSettled([
        dashboardRequest,
        prDetailsRequest,
        assetSummaryRequest,
      ]);

      const dashboardRoot =
        dashboardResult.status === "fulfilled"
          ? dashboardResult.value?.data?.response || dashboardResult.value?.data || {}
          : {};

      const prPayload =
        prResult.status === "fulfilled"
          ? prResult.value?.data?.response || prResult.value?.data || {}
          : {};

      const assetPayload =
        assetResult.status === "fulfilled"
          ? assetResult.value?.data?.response || assetResult.value?.data || {}
          : {};

      const withPrDetails = mergePrDetails(dashboardRoot, prPayload);
      setDashboardResponse(mergeAssetSummary(withPrDetails, assetPayload));
    } catch (error) {
      console.error("Buyer admin dashboard failed to load:", error);
      setDashboardResponse({});
    } finally {
      setLoadingState(false);
    }
  }, [authToken, orgToken]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  return (
    <NewBuyerDashboard
      dashboardResponse={dashboardResponse}
      loadingState={loadingState}
      navigate={navigate}
      spendItems={[]}
      spendTableHeadings={[]}
      spendPageNumber={1}
      disableAssetSummaryFetch={true}
    />
  );
};

export default BuyerAdminDashboardTab;
